import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HelmetProvider } from 'react-helmet-async';
import { createBrowserRouter, RouterProvider, Outlet } from "react-router-dom";
import ScrollToTop from "./components/ScrollToTop";
import PlausibleAnalytics from "./components/PlausibleAnalytics";
import { AuthProvider } from "./contexts/AuthContext";
import Index from "./pages/Index";
import RedirectHandler from "./components/RedirectHandler";

// Public routes that are not the landing page – lazy-loaded so they stay out of the initial bundle.
const Katalog = lazy(() => import("./pages/Katalog"));
const VendorDetail = lazy(() => import("./pages/VendorDetail"));
const AktivityKatalog = lazy(() => import("./pages/AktivityKatalog"));
const ActivityDetail = lazy(() => import("./pages/ActivityDetail"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Pridat = lazy(() => import("./pages/Pridat"));
const UpravitProfil = lazy(() => import("./pages/UpravitProfil"));
const Kontakt = lazy(() => import("./pages/Kontakt"));
const Novinky = lazy(() => import("./pages/Novinky"));
const Odhlasit = lazy(() => import("./pages/Odhlasit"));

// Admin bundle – split off completely from public pages.
const AdminLayout = lazy(() => import("./components/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const VendorsList = lazy(() => import("./pages/admin/VendorsList"));
const VendorEdit = lazy(() => import("./pages/admin/VendorEdit"));
const ActivitiesList = lazy(() => import("./pages/admin/ActivitiesList"));
const ActivityEdit = lazy(() => import("./pages/admin/ActivityEdit"));
const Analytics = lazy(() => import("./pages/admin/Analytics"));
const Login = lazy(() => import("./pages/admin/Login"));
const UsersList = lazy(() => import("./pages/admin/UsersList"));
const EmailLogs = lazy(() => import("./pages/admin/EmailLogs"));
const NewsletterList = lazy(() => import("./pages/admin/NewsletterList"));
const NewsletterForm = lazy(() => import("./pages/admin/NewsletterForm"));
const NewsletterDetail = lazy(() => import("./pages/admin/NewsletterDetail"));

const PageFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
  </div>
);

const withSuspense = (el: JSX.Element) => (
  <Suspense fallback={<PageFallback />}>{el}</Suspense>
);
const queryClient = new QueryClient();

// Root layout that wraps all routes with providers
const RootLayout = () => (
  <AuthProvider>
    <ScrollToTop />
    <PlausibleAnalytics />
    <Outlet />
  </AuthProvider>
);

const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      { path: "/", element: <Index /> },
      { path: "/katalog", element: withSuspense(<Katalog />) },
      { path: "/stanek/:id", element: withSuspense(<VendorDetail />) },

      // Legacy redirects
      { path: "/stankari", element: <RedirectHandler /> },
      { path: "/stankari/:slug", element: <RedirectHandler /> },

      { path: "/aktivity", element: withSuspense(<AktivityKatalog />) },
      { path: "/aktivita/:id", element: withSuspense(<ActivityDetail />) },
      { path: "/pridat", element: withSuspense(<Pridat />) },
      { path: "/upravit/:slug", element: withSuspense(<UpravitProfil />) },
      { path: "/kontakt", element: withSuspense(<Kontakt />) },
      { path: "/novinky", element: withSuspense(<Novinky />) },
      { path: "/odhlasit/:token", element: withSuspense(<Odhlasit />) },

      // Admin Login - outside of protected layout
      { path: "/admin/login", element: withSuspense(<Login />) },

      // Admin Routes - protected by AdminLayout
      {
        path: "/admin",
        element: withSuspense(<AdminLayout />),
        children: [
          { index: true, element: withSuspense(<AdminDashboard />) },
          { path: "stanky", element: withSuspense(<VendorsList />) },
          { path: "stanky/:id", element: withSuspense(<VendorEdit />) },
          { path: "aktivity", element: withSuspense(<ActivitiesList />) },
          { path: "aktivity/:id", element: withSuspense(<ActivityEdit />) },
          { path: "newsletter", element: withSuspense(<NewsletterList />) },
          { path: "newsletter/nova", element: withSuspense(<NewsletterForm />) },
          { path: "newsletter/:id", element: withSuspense(<NewsletterDetail />) },
          { path: "newsletter/:id/upravit", element: withSuspense(<NewsletterForm />) },
          { path: "uzivatele", element: withSuspense(<UsersList />) },
          { path: "emaily", element: withSuspense(<EmailLogs />) },
          { path: "navstevnost", element: withSuspense(<Analytics />) },
        ],
      },

      // Catch-all route
      { path: "*", element: withSuspense(<NotFound />) },
    ],
  },
]);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <HelmetProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <RouterProvider router={router} />
      </TooltipProvider>
    </HelmetProvider>
  </QueryClientProvider>
);

export default App;
